/**
 * Activity checkout pre-population helpers (TRAV23 activities-front merge).
 *
 * The activities front kept these in its own `env-flags.ts`. RSI already has a file of
 * that name with different contents, so these live separately rather than merged.
 */

import type { ICardPayload, IHolderPayload } from '@share/common-types';
/** Direct import avoids `@utils` barrel → `utils/filters` → `@share/constants` circular init (SortTypes undefined). */
import { detectCardType } from '../../utils/booking';
import { DEFAULT_RESIDENCY } from './common';

/** Set via `VITE_PRE_POPULATE_GUEST` in `.env.*` (boolean string `true` / `false`). */
export const PRE_POPULATE_GUEST = import.meta.env.VITE_PRE_POPULATE_GUEST === 'true';

/** 0 → `a`, 1 → `b`, … 25 → `z`, 26 → `aa`, … (spreadsheet-style, no separator). */
function guestLetterSuffix(guestIndex: number): string {
    let n = guestIndex + 1;
    let s = '';
    while (n > 0) {
        const rem = (n - 1) % 26;
        s = String.fromCharCode(97 + rem) + s;
        n = Math.floor((n - 1) / 26);
    }
    return s;
}

/** First guest `testa`, second `testb`, … (given and surname use the same token). */
export function guestPrePopulatedName(guestIndex: number): string {
    return `test${guestLetterSuffix(guestIndex)}`;
}

const emptyHolder = (): IHolderPayload => ({
    namePrefix: 'Mr',
    givenName: '',
    surName: '',
    email: '',
    phone: '',
    addressLine: '',
    city: '',
    stateCode: '',
    postalCode: '',
    country: DEFAULT_RESIDENCY,
    countryCode: DEFAULT_RESIDENCY,
});

export function getCheckoutHolderInitialState(): IHolderPayload {
    if (!PRE_POPULATE_GUEST) {
        return emptyHolder();
    }
    return {
        ...emptyHolder(),
        givenName: guestPrePopulatedName(0),
        surName: guestPrePopulatedName(0),
        email: 'test@test.com',
        phone: '1231231234',
        addressLine: 'test',
        city: 'test',
        stateCode: 'FL',
        postalCode: '12345',
    };
}

const emptyCard = (): ICardPayload => ({
    addressLine: '',
    addressLine2: null,
    cvv: '',
    cardNumber: '',
    cardType: '',
    city: '',
    state: '',
    country: DEFAULT_RESIDENCY,
    countryCode: DEFAULT_RESIDENCY,
    expireDate: '',
    holderName: '',
    phone: '',
    zipCode: '',
});

/** Stripe-style test values when `VITE_PRE_POPULATE_GUEST=true` (matches `PaymentForm` MM + YYYY → `expireDate`). */
export function getCheckoutCardInitialState(): ICardPayload {
    if (!PRE_POPULATE_GUEST) {
        return emptyCard();
    }
    const testPan = '4242424242424242';
    return {
        ...emptyCard(),
        cardNumber: testPan,
        /** 11 + 2032 — Nov 2032 (UI shows 11/32) */
        expireDate: '112032',
        cvv: '1234',
        cardType: detectCardType(testPan) ?? '',
    };
}
